import {messages} from '@smartmixin/i18n';

/**
 * IBM icon code to its spoken name, shared by both apps (FE #244).
 *
 * The backend computes the code (`apps/core/utils/weather/icons.py`,
 * `compute.py::compute_weather_icon`) and sends the bare integer on
 * `HourlyWeatherForecastItem.ICON`, `DailyPeriodSummary.ICON`,
 * `DailyWeatherForecastItem.ICON` and the FCM payload's `data.icon`. It has
 * English names for all 48 codes and sends none of them, which is why the sky
 * condition — the core datum of a weather product — had no text form in either
 * app until this module existed.
 *
 * Only the id-to-text half is shared. `getIconImage` stays duplicated in the
 * two apps on purpose: its return types are platform-irreconcilable, an opaque
 * `require()` asset handle on RN against a hashed URL string from Vite, and
 * forcing them together would produce a shim. Same reasoning as
 * `TwilightBand.tsx`, which is 38% identical across the apps and deliberately
 * not shared.
 *
 * A `Record` rather than a switch, following `Moon/phaseName.ts`: a missing
 * code is then a compile error instead of an `undefined` that `formatMessage`
 * throws on. The key type is the explicit union of the 48 codes, **not**
 * `Record<number, …>` — an index signature would accept a table with holes and
 * hand back `M` for any integer, so it buys none of that guarantee. Returns the
 * descriptor, not a formatted string, because every branch resolves to one —
 * `Units.formatBearingSpeech` takes an `intl` only because it can return
 * `null`.
 *
 * Five day/night pairs share a name, exactly as they do upstream: the glyph
 * carries the sun-vs-moon distinction, and every surface that shows the icon
 * already speaks the time.
 */
/**
 * The IBM icon code space the backend emits: 0-47 inclusive.
 *
 * A `const` tuple rather than a hand-written union of 48 literals purely so the
 * source stays readable; the derived type is identical.
 */
export const WEATHER_ICON_CODES = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
  22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40,
  41, 42, 43, 44, 45, 46, 47,
] as const;

export type WeatherIconCode = (typeof WEATHER_ICON_CODES)[number];

const CONDITION_MESSAGES: Record<
  WeatherIconCode,
  (typeof messages.weatherConditions)[keyof typeof messages.weatherConditions]
> = {
  0: messages.weatherConditions.tornado,
  1: messages.weatherConditions.tropicalStorm,
  2: messages.weatherConditions.hurricane,
  3: messages.weatherConditions.strongStorms,
  4: messages.weatherConditions.thunderstorms,
  5: messages.weatherConditions.rainSnow,
  6: messages.weatherConditions.rainSleet,
  7: messages.weatherConditions.wintryMix,
  8: messages.weatherConditions.freezingDrizzle,
  9: messages.weatherConditions.drizzle,
  10: messages.weatherConditions.freezingRain,
  11: messages.weatherConditions.showers,
  12: messages.weatherConditions.rain,
  13: messages.weatherConditions.flurries,
  14: messages.weatherConditions.snowShowers,
  15: messages.weatherConditions.blowingSnow,
  16: messages.weatherConditions.snow,
  17: messages.weatherConditions.hail,
  18: messages.weatherConditions.sleet,
  19: messages.weatherConditions.blowingDust,
  20: messages.weatherConditions.foggy,
  21: messages.weatherConditions.haze,
  22: messages.weatherConditions.smoke,
  23: messages.weatherConditions.breezy,
  24: messages.weatherConditions.windy,
  25: messages.weatherConditions.frigid,
  26: messages.weatherConditions.cloudy,
  // 27/28, 29/30, 38/47, 39/45, 41/46 — night first, then day.
  27: messages.weatherConditions.mostlyCloudy,
  28: messages.weatherConditions.mostlyCloudy,
  29: messages.weatherConditions.partlyCloudy,
  30: messages.weatherConditions.partlyCloudy,
  31: messages.weatherConditions.clear,
  32: messages.weatherConditions.sunny,
  33: messages.weatherConditions.mostlyClear,
  34: messages.weatherConditions.mostlySunny,
  35: messages.weatherConditions.mixedRainAndHail,
  36: messages.weatherConditions.hot,
  37: messages.weatherConditions.isolatedThunderstorms,
  38: messages.weatherConditions.scatteredThunderstorms,
  39: messages.weatherConditions.scatteredShowers,
  40: messages.weatherConditions.heavyRain,
  41: messages.weatherConditions.scatteredSnowShowers,
  42: messages.weatherConditions.heavySnow,
  43: messages.weatherConditions.blizzard,
  // 44 is the backend's real NOT_AVAILABLE code, with its own `44.png` asset.
  // It resolves to the same wording as an out-of-range value, but it is not the
  // same path: the fallback below also covers `undefined`, a code the backend
  // has not learned yet, and the separate `na.png` case in the apps.
  44: messages.weatherConditions.notAvailable,
  45: messages.weatherConditions.scatteredShowers,
  46: messages.weatherConditions.scatteredSnowShowers,
  47: messages.weatherConditions.scatteredThunderstorms,
};

export const getConditionMessage = (iconId: number | null | undefined) =>
  // Falling back to a *named* condition would assert a specific wrong sky, so
  // the generic wording is the honest degradation — the same call
  // `Moon.getPhaseMessage` makes with `astral.moon`.
  (iconId != null && CONDITION_MESSAGES[iconId as WeatherIconCode]) ||
  messages.weatherConditions.notAvailable;

export {CONDITION_MESSAGES};
